import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWeb3React } from '@web3-react/core'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { RateState } from '../../types'
import { StyledExchangeBtn } from './styled'
import { useGA, useIsValidNetwork } from '~/hooks'
import { useSpotSwapAmount, useSpotSwapPair } from '~/state/dex/hooks'
import IconLock from '~/assets/images/lock-white.svg'
import { AddressZero } from '@ethersproject/constants'
import { useSettlementFeeValidator } from '~/components/Exchange/hooks'
import Space from '~/components/Space'
import NotificationBar from '~/components/NotificationBar'

export interface PreviewButtonProps {
  handlePreview: fn
  validateInputAmountProxy: fn
  validateInputAmountSendBySelfProxy: fn
  rateState: RateState
  shouldApprove: boolean
  minAmountOut: bigint
  isAmountUnderMin: boolean
  isAmountOverMax: boolean
}

const PreviewButton = ({
  handlePreview,
  rateState,
  shouldApprove,
  validateInputAmountProxy,
  validateInputAmountSendBySelfProxy,
  minAmountOut,
  isAmountUnderMin,
  isAmountOverMax,
}: PreviewButtonProps) => {
  const { t } = useTranslation()
  const [inputToken] = useSpotSwapPair()
  const { inputAmount, outputAmount } = useSpotSwapAmount()
  const isTakerETH = inputToken?.address === AddressZero

  const { trackElClickCurring } = useGA()
  const { account } = useWeb3React()
  const isValidNetwork = useIsValidNetwork()
  const isWalletConnected = !!account
  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT)
  const toggleSwitchNetworkModal = useModalToggle(
    ApplicationModal.SWITCH_NETWORK,
  )
  const isSettlementFeeValid = useSettlementFeeValidator(minAmountOut)
  const [isInsufficientMinerFee, setIsInsufficientMinerFee] = useState<boolean>(
    false,
  )
  const [showApproveLock, setShowApproveLock] = useState<boolean>(false)

  const disabledByAmount = useMemo(
    () =>
      !inputAmount ||
      isNaN(Number(inputAmount)) ||
      Number(inputAmount) === 0 ||
      !outputAmount ||
      isNaN(Number(outputAmount)) ||
      Number(outputAmount) === 0,
    [inputAmount, outputAmount],
  )

  const buttonText = useMemo(() => {
    setIsInsufficientMinerFee(false)
    setShowApproveLock(false)
    // 未连接钱包：连接钱包
    if (!isWalletConnected) {
      return t('connect')
    }
    if (!isValidNetwork) {
      return t('switch_network')
    }
    if (isAmountUnderMin) {
      return t('spot_swap_preview_btn_amount_under_min')
    }
    if (isAmountOverMax) {
      return t('spot_swap_preview_btn_amount_over_max')
    }
    // 开启代上链，但交易额太小，不足以支付结算费
    if (!isSettlementFeeValid) {
      setIsInsufficientMinerFee(true)
      return t('spotswap_swap_amount_too_low_btn')
    }
    // 已连接钱包未询价：输入金额
    if (!inputAmount && !outputAmount) {
      return t('enter_amount_btn')
    }
    // 已连接钱包询价中：询价中
    if (inputAmount && !outputAmount) {
      return t('fetching_quote_btn')
    }
    // 校验余额
    const error: string = isTakerETH
      ? validateInputAmountSendBySelfProxy()
      : validateInputAmountProxy()
    if (error) {
      switch (error) {
        case 'insufficient_balance_with_toggle_modal':
          setIsInsufficientMinerFee(shouldApprove)
          return shouldApprove
            ? t('preview_$symbol_insufficient_balance', {
                symbol: inputToken?.symbol,
              })
            : t('review_btn')
        case '$symbol_insufficient_balance':
          // 特殊处理 $symbol_insufficient_balance 情况下的文案
          setIsInsufficientMinerFee(true)
          return t('preview_$symbol_insufficient_balance', {
            symbol: inputToken?.symbol,
          })
        default:
          setIsInsufficientMinerFee(true)
          return t(error)
      }
    }
    // 已连接钱包但代币未授权：授权Token
    if (shouldApprove) {
      // 仅在展示 授权Token 文案时显示 IconLock
      setShowApproveLock(true)
      return t('approve_to_permit2', { symbol: inputToken?.symbol })
    }
    return t('review_btn')
  }, [
    isWalletConnected,
    inputAmount,
    outputAmount,
    shouldApprove,
    t,
    minAmountOut,
    isSettlementFeeValid,
    isValidNetwork,
    isAmountUnderMin,
    isAmountOverMax,
  ])

  const normalState =
    rateState === RateState.OK || rateState === RateState.REFRESH_LOADING
  const disabled = useMemo(() => {
    return (
      isWalletConnected &&
      isValidNetwork &&
      (!normalState || disabledByAmount || isInsufficientMinerFee)
    )
  }, [
    normalState,
    disabledByAmount,
    isWalletConnected,
    isInsufficientMinerFee,
    isValidNetwork,
  ])

  const handleClick = (e) => {
    if (!isWalletConnected) {
      trackElClickCurring(e)(toggleWalletModal)
    } else if (!isValidNetwork) {
      toggleSwitchNetworkModal()
    } else {
      handlePreview()
    }
  }

  return (
    <>
      {!isValidNetwork && (
        <>
          <Space height={15} />
          <NotificationBar
            level="warning"
            text={t('unmatched_network_swap_warning')}
            showLink={false}
            showClose={false}
          />
        </>
      )}
      <StyledExchangeBtn
        className={`exchange-btn ${disabled && 'disabled'}`}
        disabled={disabled}
        data-trackid="webtl_walletconnect"
        data-trackdata={JSON.stringify({ from: 'instant' })}
        onClick={handleClick}
      >
        {buttonText}
        {shouldApprove && showApproveLock && <img src={IconLock} />}
      </StyledExchangeBtn>
    </>
  )
}

export default PreviewButton
